import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserProfile } from '@lizrd/contracts';
import { Avatar } from './Avatar';

/**
 * The header account menu: the signed-in user's avatar + name sits at the far
 * right and opens a dropdown with Profile Settings and Sign Out. Closes on
 * outside click or Escape; styled with design tokens so it flips with the theme.
 */
export function UserMenu({ user, onSignOut }: { user: UserProfile; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-sm border border-border py-1 pl-1 pr-2 text-haze transition-colors hover:border-border-strong hover:bg-surface-2 hover:text-bone"
      >
        <Avatar name={user.name} email={user.email} size={26} />
        <span className="hidden max-w-[160px] truncate text-sm sm:inline">
          {user.name?.trim() || user.email}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-md border border-hairline bg-surface shadow-xl"
        >
          <div className="flex items-center gap-2.5 border-b border-hairline px-3 py-3">
            <Avatar name={user.name} email={user.email} size={34} />
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-bone">
                {user.name?.trim() || 'Your account'}
              </div>
              <div className="truncate text-xs text-muted">{user.email}</div>
            </div>
          </div>
          <div className="py-1">
            {user.organizationKind !== 'SANDBOX' && (
              <button
                role="menuitem"
                onClick={() => run(() => navigate('/profile'))}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-haze transition-colors hover:bg-surface-2 hover:text-bone"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="flex-none text-muted"
                >
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21a8 8 0 0 1 16 0" />
                </svg>
                Profile Settings
              </button>
            )}
            <button
              role="menuitem"
              onClick={() => run(onSignOut)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-danger transition-colors hover:bg-danger-dim"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="flex-none"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="m16 17 5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
